import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../integrations/supabase/client';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { DocumentUpload } from '../components/comercial/DocumentUpload';
import { 
  formatCPFCNPJ, 
  formatCPF,
  formatRG, 
  formatPhone, 
  formatCEP,
  formatCurrency,
  parseCurrency
} from '../utils/masks';

interface ProcessoInfo {
  id: string;
  titulo: string;
  tipo: string;
}

export default function PublicFormPage() {
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);

  const [processo, setProcesso] = useState<ProcessoInfo | null>(null);
  const [tipoParte, setTipoParte] = useState<string>('');

  const [formData, setFormData] = useState({
    nome_completo: '',
    cpf_cnpj: '',
    email: '',
    telefone: '',
    endereco: {
      cep: '',
      rua: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: 'SP',
    },
    dados_bancarios: {
      banco: '',
      agencia: '',
      conta: '',
      tipo_conta: 'corrente',
      chave_pix: '',
    },
    dados_pessoais: {
      rg: '',
      data_nascimento: '',
      renda_familiar: '',
      estado_civil: 'solteiro',
      regime_casamento: '',
      garantia_locaticia: '',
    },
    dados_conjuge: {
      nome: '',
      email: '',
      telefone: '',
      rg: '',
      cpf: '',
      data_nascimento: '',
      endereco: '',
      cep: '',
      bairro: '',
      cidade: '',
      renda: '',
      estado_civil: '',
      regime_casamento: '',
    },
    dados_imovel: {
      endereco: '',
      numero: '',
      complemento: '',
      vaga: '',
      cep: '',
      bairro: '',
      cidade: '',
      condominio: '',
      matricula: '',
      codigo_cartografico: '',
    },
    documentos: [] as any[],
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('Token não fornecido');
      setValidating(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('validate-form-token', {
        body: { token },
      });

      if (error || !data.valid) {
        setError(data?.error || 'Token inválido');
        setValidating(false);
        return;
      }

      setProcesso(data.processo);
      setTipoParte(data.tipo_parte);
      setValidating(false);

    } catch (err: any) {
      console.error('Erro ao validar token:', err);
      setError(err.message || 'Erro ao validar formulário');
      setValidating(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEnderecoChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      endereco: {
        ...prev.endereco,
        [field]: value,
      },
    }));
  };

  const handleDadosBancariosChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      dados_bancarios: {
        ...prev.dados_bancarios,
        [field]: value,
      },
    }));
  };

  // Função auxiliar para formatar datas corretamente (sem problemas de timezone)
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    // Garante que a data seja interpretada como local
    return dateString.split('T')[0];
  };

  const handleDadosPessoaisChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      dados_pessoais: {
        ...prev.dados_pessoais,
        [field]: value,
      },
    }));
  };

  const handleDadosConjugeChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      dados_conjuge: {
        ...prev.dados_conjuge,
        [field]: value,
      },
    }));
  };

  const handleDadosImovelChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      dados_imovel: {
        ...prev.dados_imovel,
        [field]: value,
      },
    }));
  };

  const handleDocumentUpload = (tipo: string, url: string, fileName: string) => {
    setFormData(prev => {
      const existingDocs = prev.documentos.filter(d => d.tipo !== tipo);
      if (url) {
        return {
          ...prev,
          documentos: [...existingDocs, { tipo, url, nome: fileName }]
        };
      }
      return { ...prev, documentos: existingDocs };
    });
  };

  // Efeito para contagem regressiva e fechamento automático
  useEffect(() => {
    if (success && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (success && countdown === 0) {
      // Tenta fechar a janela/aba
      window.close();
      // Se não conseguir fechar (por segurança do navegador), redireciona
      setTimeout(() => {
        window.location.href = 'about:blank';
      }, 100);
    }
  }, [success, countdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('submit-public-form', {
        body: {
          token,
          dados: formData,
        },
      });

      if (error || !data.success) {
        throw new Error(data?.error || 'Erro ao enviar formulário');
      }

      setSuccess(true);
      toast({
        title: 'Sucesso!',
        description: 'Formulário enviado com sucesso. Obrigado!',
      });

    } catch (err: any) {
      console.error('Erro ao enviar formulário:', err);
      toast({
        title: 'Erro',
        description: err.message || 'Erro ao enviar formulário',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Validando formulário...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 to-destructive/10">
        <div className="max-w-md w-full mx-4 p-8 bg-card rounded-lg shadow-lg text-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">Formulário Inválido</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/')}>Voltar para Home</Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-md w-full mx-4 p-8 bg-card rounded-lg shadow-lg text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Formulário Enviado!</h1>
          <p className="text-muted-foreground mb-4">
            Obrigado por preencher o formulário. Nossa equipe entrará em contato em breve.
          </p>
          <p className="text-sm text-muted-foreground">
            Esta página será fechada automaticamente em{' '}
            <span className="font-bold text-primary">{countdown}</span> segundos...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header com Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">🏠 Fox Vendas</h1>
          <p className="text-muted-foreground">Formulário de Cadastro de Dados</p>
        </div>

        <div className="bg-card rounded-lg shadow-lg p-8">
          {/* Informações do Processo */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-2">{processo?.titulo}</h2>
            <p className="text-blue-800 dark:text-blue-200">
              Você está preenchendo os dados como: <strong className="text-primary">{tipoParte}</strong>
            </p>
          </div>

          {/* Indicador de Progresso */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progresso do Cadastro</span>
              <span className="text-sm text-muted-foreground">1 de 3 etapas</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-primary h-2 rounded-full" style={{ width: '33%' }}></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dados Pessoais */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Dados Pessoais</h2>
              
              <div>
                <label className="block text-sm font-medium mb-2">Nome Completo *</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                  value={formData.nome_completo}
                  onChange={(e) => handleInputChange('nome_completo', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">CPF/CNPJ *</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                  value={formatCPFCNPJ(formData.cpf_cnpj)}
                  onChange={(e) => handleInputChange('cpf_cnpj', e.target.value.replace(/\D/g, ''))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">RG *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formatRG(formData.dados_pessoais.rg)}
                    onChange={(e) => handleDadosPessoaisChange('rg', e.target.value.replace(/\D/g, ''))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Data de Nascimento *</label>
                  <input
                    type="date"
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formatDateForInput(formData.dados_pessoais.data_nascimento)}
                    onChange={(e) => handleDadosPessoaisChange('data_nascimento', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Renda Familiar *</label>
                  <input
                    type="text"
                    required
                    placeholder="R$ 0,00"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formatCurrency(formData.dados_pessoais.renda_familiar)}
                    onChange={(e) => handleDadosPessoaisChange('renda_familiar', parseCurrency(e.target.value).toString())}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Estado Civil *</label>
                  <select
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.dados_pessoais.estado_civil}
                    onChange={(e) => handleDadosPessoaisChange('estado_civil', e.target.value)}
                  >
                    <option value="solteiro">Solteiro</option>
                    <option value="casado">Casado</option>
                    <option value="divorciado">Divorciado</option>
                    <option value="viuvo">Viúvo</option>
                    <option value="uniao_estavel">União Estável</option>
                  </select>
                </div>
              </div>

              {['casado', 'uniao_estavel'].includes(formData.dados_pessoais.estado_civil) && (
                <div>
                  <label className="block text-sm font-medium mb-2">Regime de Casamento *</label>
                  <select
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.dados_pessoais.regime_casamento}
                    onChange={(e) => handleDadosPessoaisChange('regime_casamento', e.target.value)}
                  >
                    <option value="">Selecione</option>
                    <option value="comunhao_parcial">Comunhão Parcial de Bens</option>
                    <option value="comunhao_universal">Comunhão Universal de Bens</option>
                    <option value="separacao_total">Separação Total de Bens</option>
                    <option value="participacao_final">Participação Final nos Aquestos</option>
                  </select>
                </div>
              )}

              {tipoParte === 'locatario' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Garantia Locatícia *</label>
                  <select
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.dados_pessoais.garantia_locaticia}
                    onChange={(e) => handleDadosPessoaisChange('garantia_locaticia', e.target.value)}
                  >
                    <option value="">Selecione</option>
                    <option value="fiador">Fiador</option>
                    <option value="caucao">Caução</option>
                    <option value="seguro_fianca">Seguro Fiança</option>
                    <option value="titulo_capitalizacao">Título de Capitalização</option>
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Telefone *</label>
                  <input
                    type="tel"
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formatPhone(formData.telefone)}
                    onChange={(e) => handleInputChange('telefone', e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>
            </div>

            {/* Documentos Pessoais */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Documentos Pessoais</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DocumentUpload
                  label="RG"
                  documentType="rg"
                  onUpload={(url, name) => handleDocumentUpload('rg', url, name)}
                  currentFile={formData.documentos.find(d => d.tipo === 'rg')}
                  required
                />

                <DocumentUpload
                  label="CPF"
                  documentType="cpf"
                  onUpload={(url, name) => handleDocumentUpload('cpf', url, name)}
                  currentFile={formData.documentos.find(d => d.tipo === 'cpf')}
                  required
                />

                <DocumentUpload
                  label="Comprovante de Endereço"
                  documentType="comprovante_endereco"
                  onUpload={(url, name) => handleDocumentUpload('comprovante_endereco', url, name)}
                  currentFile={formData.documentos.find(d => d.tipo === 'comprovante_endereco')}
                  required
                />

                {tipoParte !== 'locador' && (
                  <DocumentUpload
                    label="Comprovante de Renda"
                    documentType="comprovante_renda"
                    onUpload={(url, name) => handleDocumentUpload('comprovante_renda', url, name)}
                    currentFile={formData.documentos.find(d => d.tipo === 'comprovante_renda')}
                    required
                  />
                )}

                <DocumentUpload
                  label="Certidão de Estado Civil"
                  documentType="certidao_estado_civil"
                  onUpload={(url, name) => handleDocumentUpload('certidao_estado_civil', url, name)}
                  currentFile={formData.documentos.find(d => d.tipo === 'certidao_estado_civil')}
                />
              </div>
            </div>

            {/* Dados do Cônjuge (condicional) */}
            {['casado', 'uniao_estavel'].includes(formData.dados_pessoais.estado_civil) && (
              <>
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold">Dados do Cônjuge</h2>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome Completo *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_conjuge.nome}
                      onChange={(e) => handleDadosConjugeChange('nome', e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                        value={formData.dados_conjuge.email}
                        onChange={(e) => handleDadosConjugeChange('email', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Celular *</label>
                      <input
                        type="tel"
                        required
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                        value={formatPhone(formData.dados_conjuge.telefone)}
                        onChange={(e) => handleDadosConjugeChange('telefone', e.target.value.replace(/\D/g, ''))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">RG *</label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                        value={formatRG(formData.dados_conjuge.rg)}
                        onChange={(e) => handleDadosConjugeChange('rg', e.target.value.replace(/\D/g, ''))}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">CPF *</label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                        value={formatCPF(formData.dados_conjuge.cpf)}
                        onChange={(e) => handleDadosConjugeChange('cpf', e.target.value.replace(/\D/g, ''))}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Data de Nascimento *</label>
                      <input
                        type="date"
                        required
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                        value={formatDateForInput(formData.dados_conjuge.data_nascimento)}
                        onChange={(e) => handleDadosConjugeChange('data_nascimento', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Renda *</label>
                    <input
                      type="text"
                      required
                      placeholder="R$ 0,00"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formatCurrency(formData.dados_conjuge.renda)}
                      onChange={(e) => handleDadosConjugeChange('renda', parseCurrency(e.target.value).toString())}
                    />
                  </div>
                </div>

                {/* Documentos do Cônjuge */}
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold">Documentos do Cônjuge</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DocumentUpload
                      label="RG do Cônjuge"
                      documentType="conjuge_rg"
                      onUpload={(url, name) => handleDocumentUpload('conjuge_rg', url, name)}
                      currentFile={formData.documentos.find(d => d.tipo === 'conjuge_rg')}
                    />

                    <DocumentUpload
                      label="CPF do Cônjuge"
                      documentType="conjuge_cpf"
                      onUpload={(url, name) => handleDocumentUpload('conjuge_cpf', url, name)}
                      currentFile={formData.documentos.find(d => d.tipo === 'conjuge_cpf')}
                    />

                    <DocumentUpload
                      label="Comprovante de Renda do Cônjuge"
                      documentType="conjuge_comprovante_renda"
                      onUpload={(url, name) => handleDocumentUpload('conjuge_comprovante_renda', url, name)}
                      currentFile={formData.documentos.find(d => d.tipo === 'conjuge_comprovante_renda')}
                    />

                    <DocumentUpload
                      label="Certidão de Estado Civil"
                      documentType="conjuge_certidao_estado_civil"
                      onUpload={(url, name) => handleDocumentUpload('conjuge_certidao_estado_civil', url, name)}
                      currentFile={formData.documentos.find(d => d.tipo === 'conjuge_certidao_estado_civil')}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Endereço */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Endereço</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">CEP</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formatCEP(formData.endereco.cep)}
                    onChange={(e) => handleEnderecoChange('cep', e.target.value.replace(/\D/g, ''))}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Rua</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.endereco.rua}
                    onChange={(e) => handleEnderecoChange('rua', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Número</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.endereco.numero}
                    onChange={(e) => handleEnderecoChange('numero', e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Complemento</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.endereco.complemento}
                    onChange={(e) => handleEnderecoChange('complemento', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Bairro</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.endereco.bairro}
                    onChange={(e) => handleEnderecoChange('bairro', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Cidade</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.endereco.cidade}
                    onChange={(e) => handleEnderecoChange('cidade', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Estado</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                    value={formData.endereco.estado}
                    onChange={(e) => handleEnderecoChange('estado', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Dados Bancários - Oculto para Locatário */}
            {tipoParte !== 'locatario' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">
                  {tipoParte === 'locador' ? 'Dados Bancários *' : 'Dados Bancários (Opcional)'}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Banco {tipoParte === 'locador' && '*'}
                    </label>
                    <input
                      type="text"
                      required={tipoParte === 'locador'}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_bancarios.banco}
                      onChange={(e) => handleDadosBancariosChange('banco', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Agência {tipoParte === 'locador' && '*'}
                    </label>
                    <input
                      type="text"
                      required={tipoParte === 'locador'}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_bancarios.agencia}
                      onChange={(e) => handleDadosBancariosChange('agencia', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Conta {tipoParte === 'locador' && '*'}
                    </label>
                    <input
                      type="text"
                      required={tipoParte === 'locador'}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_bancarios.conta}
                      onChange={(e) => handleDadosBancariosChange('conta', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Tipo de Conta {tipoParte === 'locador' && '*'}
                    </label>
                    <select
                      required={tipoParte === 'locador'}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_bancarios.tipo_conta}
                      onChange={(e) => handleDadosBancariosChange('tipo_conta', e.target.value)}
                    >
                      <option value="">Selecione</option>
                      <option value="corrente">Corrente</option>
                      <option value="poupanca">Poupança</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      Chave PIX {tipoParte === 'locador' && '*'}
                    </label>
                    <input
                      type="text"
                      required={tipoParte === 'locador'}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      placeholder="CPF, CNPJ, Email, Telefone ou Chave Aleatória"
                      value={formData.dados_bancarios.chave_pix}
                      onChange={(e) => handleDadosBancariosChange('chave_pix', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Dados do Imóvel (quando necessário) */}
            {processo?.tipo === 'locacao' && tipoParte === 'locador' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Dados do Imóvel</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Endereço do Imóvel *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_imovel.endereco}
                      onChange={(e) => handleDadosImovelChange('endereco', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Número</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_imovel.numero}
                      onChange={(e) => handleDadosImovelChange('numero', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Complemento</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_imovel.complemento}
                      onChange={(e) => handleDadosImovelChange('complemento', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Número da Vaga</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_imovel.vaga}
                      onChange={(e) => handleDadosImovelChange('vaga', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">CEP *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formatCEP(formData.dados_imovel.cep)}
                      onChange={(e) => handleDadosImovelChange('cep', e.target.value.replace(/\D/g, ''))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Bairro *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_imovel.bairro}
                      onChange={(e) => handleDadosImovelChange('bairro', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Cidade *</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_imovel.cidade}
                      onChange={(e) => handleDadosImovelChange('cidade', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Condomínio</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_imovel.condominio}
                      onChange={(e) => handleDadosImovelChange('condominio', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Nº da Matrícula</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_imovel.matricula}
                      onChange={(e) => handleDadosImovelChange('matricula', e.target.value)}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Código Cartográfico</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                      value={formData.dados_imovel.codigo_cartografico}
                      onChange={(e) => handleDadosImovelChange('codigo_cartografico', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="pt-6 border-t">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar Formulário'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
